// ── MayorStories cluster (the "mayor elections are on" announcement) ──
// Extracted verbatim from MapCanvas.tsx as TWO hooks. They cannot be merged:
// `useMayorStoriesGate` must run BEFORE useMapQueries (which needs the
// "blocked by onboarding" flag to decide whether to query at all), while the
// dialog itself needs that query's result. Each is called from the original
// position, so the effect order is unchanged.
//
// Bodies are byte-identical to the originals; closure dependencies are
// parameters under their ORIGINAL names.
//
// NOTE: `useMayorStories`'s first effect also carries the city-hall-state
// prefetch. That prefetch lives in the SAME effect body as the election-state
// prefetch upstream; splitting it out would turn one effect into two and change
// the commit-time side-effect count, so it moved along unchanged.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { QueryClient } from '@tanstack/react-query';
import type { MapObject } from '../../models/mapObject';
import type { UserData } from '../../models/user';
import type { ElectionDialogState } from '../../models/apiTypes';
import type { MapModalName, MapModalPayload } from '../../stores/useMapModalStore';
import {
  dismissElectionDialogElectionsDismissDialogPost,
  getCityhallStateCityhallStateGet,
} from '../../client';
import { showAlert } from '../../utils/telegram/alert';
import { fetchElectionDialogState } from './useMapData';

async function fetchCityHallState() {
  const { data } = await getCityhallStateCityhallStateGet({ throwOnError: true });
  return data;
}

export interface UseMayorStoriesGateParams {
  /** `hasOnboardingCompleted()` result — by value. */
  onboardingCompleted: boolean;
}

/**
 * Newcomers must finish onboarding before MayorStories may appear. Once the
 * onboarding completes (even mid-session) the gate opens; the host then waits
 * until no other map modal is up, so the announcement never covers the
 * onboarding reward / first-house flow.
 * Returns the flag that gates BOTH the election query (useMapQueries) and the
 * dialog effect below.
 */
export function useMayorStoriesGate({
  onboardingCompleted,
}: UseMayorStoriesGateParams): { isMayorStoriesBlockedByOnboarding: boolean } {
  return { isMayorStoriesBlockedByOnboarding: !onboardingCompleted };
}

export interface UseMayorStoriesParams {
  /** By value, re-read every render (also the effect deps). */
  userData: UserData | undefined;
  isMayorStoriesBlockedByOnboarding: boolean;
  electionDialogStateData: ElectionDialogState | undefined;
  cityhalls: MapObject[];
  queryClient: QueryClient;
  setSelectedMapObject: (obj: MapObject | null) => void;
  setElectionCityHallScreen: (screen: import('../../screens/cityHall/types').ScreenType | null) => void;
  openModal: (name: MapModalName, payload?: Partial<MapModalPayload>) => void;
}

export interface UseMayorStoriesResult {
  showMayorStories: boolean;
  dismissMayorStories: () => void;
  handleElectionVote: () => void;
  handleElectionRegister: () => void;
}

export function useMayorStories({
  userData,
  isMayorStoriesBlockedByOnboarding,
  electionDialogStateData,
  cityhalls,
  queryClient,
  setSelectedMapObject,
  setElectionCityHallScreen,
  openModal,
}: UseMayorStoriesParams): UseMayorStoriesResult {
  const { t } = useTranslation();
  const [showMayorStories, setShowMayorStories] = useState(false);
  const electionDialogCheckedRef = useRef(false);

  useEffect(() => {
    if (!userData) return;

    queryClient.prefetchQuery({
      queryKey: ['cityhall-state'],
      queryFn: fetchCityHallState,
    }).catch((error) => {
      console.debug("Prefetch city hall state failed:", error);
    });

    if (!isMayorStoriesBlockedByOnboarding) {
      queryClient.prefetchQuery({
        queryKey: ['election-dialog-state'],
        queryFn: fetchElectionDialogState,
      }).catch((error) => {
        console.debug("Prefetch election dialog state failed:", error);
      });
    }
  }, [userData, isMayorStoriesBlockedByOnboarding, queryClient]);

  // Діалог виборів + elder-музика лише поки /user/election-dialog каже show_election_dialog.
  // Раніше ref блокував повторні проходи ефекту: після refetch з false діалог/звук лишались «ввімкненими».
  // (c) One-shot open + force-close driven by server election-dialog payload and onboarding gate
  useEffect(() => {
    if (isMayorStoriesBlockedByOnboarding) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync visibility with server dialog state
      setShowMayorStories(false);
      electionDialogCheckedRef.current = false;
      return;
    }
    if (!electionDialogStateData) return;
    if (!electionDialogStateData.show_election_dialog) {
      setShowMayorStories(false);
      // Do NOT reset checkedRef here — a slow dismiss POST + invalidate can briefly
      // refetch show=true and would reopen the announcement over City Hall.
      return;
    }
    if (electionDialogCheckedRef.current) return;
    electionDialogCheckedRef.current = true;
    setShowMayorStories(true);
  }, [electionDialogStateData, isMayorStoriesBlockedByOnboarding]);

  const dismissMayorStories = () => {
    electionDialogCheckedRef.current = true;
    setShowMayorStories(false);
    queryClient.setQueryData<ElectionDialogState>(["election-dialog-state"], (old) =>
      old ? { ...old, show_election_dialog: false } : { show_election_dialog: false }
    );
    dismissElectionDialogElectionsDismissDialogPost({ throwOnError: true })
      .catch(() => {})
      .finally(() => {
        void queryClient.invalidateQueries({ queryKey: ["election-dialog-state"] });
      });
  };

  const openCityHallScreen = (screen: 'vote_list' | 'register') => {
    const ch = cityhalls[0];
    if (!ch) {
      console.warn('[election-dialog] cityhall missing — keeping announcement open');
      showAlert(t('Try again'));
      return;
    }
    // Dismiss first so the z-[10000] announcement cannot cover City Hall (z-[2000]).
    dismissMayorStories();
    setSelectedMapObject(ch);
    setElectionCityHallScreen(screen);
    openModal('mapObject');
  };

  const handleElectionVote = () => {
    openCityHallScreen('vote_list');
  };

  const handleElectionRegister = () => {
    openCityHallScreen('register');
  };

  return { showMayorStories, dismissMayorStories, handleElectionVote, handleElectionRegister };
}
